import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { Button } from "react-bootstrap";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Placeholder from "@tiptap/extension-placeholder";
import ReviewMarkdown from "./ReviewMarkdown";
import { docToMarkdown, markdownToHtml } from "./answerMarkdown";

export type Citation = { text: string; source: string };
export type AnswerEditorHandle = {
  /** Insert a quoted passage and where it came from at the cursor. */
  insertCitation: (citation: Citation) => void;
};

const outline = `## Answer

## Reasoning and corrections

## Assumptions and limitations

## Recommended next steps

## Sources
`;

/**
 * The attorney's answer editor. Attorneys see formatted text, never Markdown;
 * the value in and out is Markdown so storage, delivery, and export are unchanged.
 */
const AnswerRichEditor = forwardRef<
  AnswerEditorHandle,
  {
    value: string;
    onChange: (markdown: string) => void;
    disabled: boolean;
    /** The passage pinned in the client's materials, if any. */
    pinned?: Citation | null;
    onSave?: () => void;
  }
>(function AnswerRichEditor(
  { value, onChange, disabled, pinned, onSave },
  ref
) {
  const [preview, setPreview] = useState(false);
  // The last Markdown this editor produced, so outside changes can be told apart.
  const emitted = useRef(value);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const onSaveRef = useRef(onSave);
  onSaveRef.current = onSave;
  const editor = useEditor({
    // Toolbar buttons reflect the formatting at the cursor.
    shouldRerenderOnTransaction: true,
    extensions: [
      StarterKit.configure({
        heading: { levels: [2, 3] },
        code: false,
        codeBlock: false,
        strike: false,
        underline: false,
        link: {
          openOnClick: false,
          autolink: true,
          HTMLAttributes: { rel: "noopener noreferrer" },
        },
      }),
      Placeholder.configure({
        placeholder:
          "Write your professional answer to the agreed request. Explain your reasoning, corrections, assumptions, and next steps.",
      }),
    ],
    content: markdownToHtml(value),
    editable: !disabled,
    editorProps: {
      attributes: {
        role: "textbox",
        "aria-multiline": "true",
        "aria-label": "Private answer draft",
        class: "answer-rich-content",
      },
      handleKeyDown: (_view, event) => {
        if ((event.metaKey || event.ctrlKey) && event.key === "s") {
          event.preventDefault();
          onSaveRef.current?.();
          return true;
        }
        return false;
      },
    },
    onUpdate: ({ editor }) => {
      const markdown = docToMarkdown(editor.getJSON() as any);
      emitted.current = markdown;
      onChangeRef.current(markdown);
    },
  });
  useEffect(() => {
    editor?.setEditable(!disabled);
  }, [editor, disabled]);
  // Replace the content only when the draft changed outside the editor
  // (recovery, "use saved version", a refreshed draft).
  useEffect(() => {
    if (!editor || value === emitted.current) return;
    emitted.current = value;
    editor.commands.setContent(markdownToHtml(value), { emitUpdate: false });
  }, [editor, value]);
  function insertCitation({ text, source }: Citation) {
    if (!editor) return;
    const paragraphs = text
      .split(/\n+/)
      .filter(Boolean)
      .map(line => ({
        type: "paragraph",
        content: [{ type: "text", text: line }],
      }));
    editor
      .chain()
      .focus()
      .insertContent([
        { type: "blockquote", content: paragraphs },
        {
          type: "paragraph",
          content: [
            {
              type: "text",
              marks: [{ type: "italic" }],
              text: `Source: ${source}`,
            },
          ],
        },
      ])
      .run();
  }
  useImperativeHandle(ref, () => ({ insertCitation }));
  const empty = !value.trim();
  const tool = (
    label: string,
    run: () => void,
    active = false,
    title?: string
  ) => (
    <Button
      size="sm"
      variant={active ? "secondary" : "light"}
      aria-pressed={active}
      title={title}
      disabled={disabled || preview || !editor}
      onMouseDown={e => e.preventDefault()}
      onClick={run}
    >
      {label}
    </Button>
  );
  const chain = () => editor!.chain().focus();
  return (
    <div className="answer-editor answer-rich-editor">
      <div
        className="answer-editor-toolbar"
        role="toolbar"
        aria-label="Answer formatting"
      >
        {tool(
          "Heading",
          () => chain().toggleHeading({ level: 2 }).run(),
          !!editor?.isActive("heading", { level: 2 })
        )}
        {tool(
          "Subheading",
          () => chain().toggleHeading({ level: 3 }).run(),
          !!editor?.isActive("heading", { level: 3 })
        )}
        {tool(
          "Bold",
          () => chain().toggleBold().run(),
          !!editor?.isActive("bold")
        )}
        {tool(
          "Italic",
          () => chain().toggleItalic().run(),
          !!editor?.isActive("italic")
        )}
        {tool(
          "Bullets",
          () => chain().toggleBulletList().run(),
          !!editor?.isActive("bulletList")
        )}
        {tool(
          "Numbers",
          () => chain().toggleOrderedList().run(),
          !!editor?.isActive("orderedList")
        )}
        {tool(
          "Quote",
          () => chain().toggleBlockquote().run(),
          !!editor?.isActive("blockquote")
        )}
        {tool(
          "Link",
          () => {
            if (editor?.isActive("link")) {
              chain().unsetLink().run();
              return;
            }
            const href = window.prompt("Link address (https://…)")?.trim();
            if (href && /^https?:\/\/\S+\.\S+/.test(href))
              chain().extendMarkRange("link").setLink({ href }).run();
          },
          !!editor?.isActive("link")
        )}
        {tool(
          "Cite",
          () => pinned && insertCitation(pinned),
          false,
          pinned
            ? "Insert the passage you pinned in the client's materials"
            : "Select text in the client's materials and choose Pin selected passage first"
        )}
        <Button
          size="sm"
          className="ms-auto"
          variant={preview ? "dark" : "outline-dark"}
          onClick={() => setPreview(!preview)}
        >
          {preview ? "Back to editing" : "Preview as client"}
        </Button>
      </div>
      <div className="answer-rich-body" hidden={preview}>
        {empty && !disabled && editor && (
          <button
            type="button"
            className="answer-outline-start"
            onClick={() => {
              // An empty paragraph under "Answer" holds the cursor.
              const html = markdownToHtml(outline).replace(
                "</h2>",
                "</h2><p></p>"
              );
              editor.commands.setContent(html, { emitUpdate: true });
              editor.commands.focus(editor.state.doc.firstChild!.nodeSize + 1);
            }}
          >
            Start with outline
          </button>
        )}
        <EditorContent editor={editor} className="attorney-draft-editor" />
      </div>
      {preview && (
        <div
          className="answer-client-preview"
          aria-label="Client answer preview"
        >
          <ReviewMarkdown>
            {value || "Your formatted answer will appear here."}
          </ReviewMarkdown>
        </div>
      )}
    </div>
  );
});
export default AnswerRichEditor;
